import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ScheduledBooking } from '../classes/WSClasses';
import { SettingsmanagerService } from './settingsmanager.service';

@Injectable({
  providedIn: 'root'
})
export class ScheduledBookingService {

  private _url: string;

  constructor(
    private _http: HttpClient,
    private settingsManager: SettingsmanagerService
  ) {
    this._url = this.settingsManager.getBaseWsUrl();
  }

  /**
   * Get all scheduled bookings
   */
  listAll(): Observable<ScheduledBooking[]> {
    return this._http.get<ScheduledBooking[]>(`${this._url}/scheduled-bookings/all`);
  }

  /**
   * Get a specific scheduled booking by ID
   */
  getById(id: number): Observable<ScheduledBooking> {
    return this._http.get<ScheduledBooking>(`${this._url}/scheduled-bookings/${id}`).pipe(
      map(obj => new ScheduledBooking(obj))
    );
  }

  /**
   * Get scheduled bookings by user ID
   */
  getByUserId(userId: number): Observable<ScheduledBooking[]> {
    return this._http.get<ScheduledBooking[]>(`${this._url}/scheduled-bookings/user/${userId}`).pipe(
      map(obj => obj.map(xx => new ScheduledBooking(xx)))
    );
  }

  /**
   * Get scheduled bookings by city ID
   */
  getByCityId(cityId: number): Observable<ScheduledBooking[]> {
    return this._http.get<ScheduledBooking[]>(`${this._url}/scheduled-bookings/city/${cityId}`).pipe(
      map(obj => obj.map(xx => new ScheduledBooking(xx)))
    );
  }

  /**
   * Get scheduled bookings by hub ID
   */
  getByHubId(hubId: number): Observable<ScheduledBooking[]> {
    return this._http.get<ScheduledBooking[]>(`${this._url}/scheduled-bookings/hub/${hubId}`).pipe(
      map(obj => obj.map(xx => new ScheduledBooking(xx)))
    );
  }

  /**
   * Get scheduled bookings by status
   */
  getByStatus(status: string): Observable<ScheduledBooking[]> {
    return this._http.get<ScheduledBooking[]>(`${this._url}/scheduled-bookings/status/${status}`).pipe(
      map(obj => obj.map(xx => new ScheduledBooking(xx)))
    );
  }

  /**
   * Create a new scheduled booking
   */
  create(scheduledBooking: Partial<ScheduledBooking>): Observable<ScheduledBooking> {
    return this._http.post<ScheduledBooking>(`${this._url}/scheduled-bookings`, scheduledBooking).pipe(
      map(obj => new ScheduledBooking(obj))
    );
  }

  /**
   * Update an existing scheduled booking
   */
  update(id: number, scheduledBooking: Partial<ScheduledBooking>): Observable<ScheduledBooking> {
    return this._http.put<ScheduledBooking>(`${this._url}/scheduled-bookings/${id}`, scheduledBooking).pipe(
      map(obj => new ScheduledBooking(obj))
    );
  }

  /**
   * Edit an existing scheduled booking (partial update)
   */
  edit(id: number, scheduledBooking: Partial<ScheduledBooking>): Observable<any> {
    return this._http.patch<any>(`${this._url}/scheduled-bookings/${id}`, scheduledBooking);
  }

  /**
   * Update booking status
   */
  updateStatus(id: number, status: string): Observable<ScheduledBooking> {
    return this._http.patch<ScheduledBooking>(`${this._url}/scheduled-bookings/${id}/status`, { status }).pipe(
      map(obj => new ScheduledBooking(obj))
    );
  }

  /**
   * Assign a device to a scheduled booking
   */
  assignDevice(bookingId: number, deviceId: number): Observable<ScheduledBooking> {
    return this._http.post<ScheduledBooking>(`${this._url}/scheduled-bookings/${bookingId}/assign-device`, { deviceId }).pipe(
      map(obj => new ScheduledBooking(obj))
    );
  }

  /**
   * Unassign device from a scheduled booking
   */
  unassignDevice(bookingId: number): Observable<ScheduledBooking> {
    return this._http.post<ScheduledBooking>(`${this._url}/scheduled-bookings/${bookingId}/disassociate-device`, {}).pipe(
      map(obj => new ScheduledBooking(obj))
    );
  }

  /**
   * Delete a scheduled booking
   */
  refund(id: number): Observable<any> {
    return this._http.post<any>(`${this._url}/scheduled-bookings/${id}/refund`, {});
  }

  /**
   * Get available devices for a specific booking
   */
  getAvailableDevices(bookingId: number): Observable<any[]> {
    return this._http.get<any[]>(`${this._url}/scheduled-bookings/${bookingId}/available-devices`);
  }

  /**
   * Get booking conflicts for a specific time range
   */
  getConflicts(startDateTime: Date, endDateTime: Date, hubId: number, modelId: number): Observable<ScheduledBooking[]> {
    const params = {
      startDateTime: startDateTime.toISOString(),
      endDateTime: endDateTime.toISOString(),
      hubId: hubId.toString(),
      modelId: modelId.toString()
    };
    return this._http.get<ScheduledBooking[]>(`${this._url}/scheduled-bookings/conflicts`, { params }).pipe(
      map(obj => obj.map(xx => new ScheduledBooking(xx)))
    );
  }

  /**
   * Cancel a scheduled booking
   */
  cancel(id: number, reason?: string): Observable<ScheduledBooking> {
    return this._http.patch<ScheduledBooking>(`${this._url}/scheduled-bookings/${id}/cancel`, { reason }).pipe(
      map(obj => new ScheduledBooking(obj))
    );
  }

  /**
   * Activate a scheduled booking
   */
  activate(id: number): Observable<ScheduledBooking> {
    return this._http.patch<ScheduledBooking>(`${this._url}/scheduled-bookings/${id}/activate`, {}).pipe(
      map(obj => new ScheduledBooking(obj))
    );
  }

  /**
   * Confirm a scheduled booking
   */
  confirm(id: number): Observable<ScheduledBooking> {
    return this._http.patch<ScheduledBooking>(`${this._url}/scheduled-bookings/${id}/confirm`, {}).pipe(
      map(obj => new ScheduledBooking(obj))
    );
  }


  /**
   * Complete a scheduled booking
   */
  complete(id: number): Observable<ScheduledBooking> {
    return this._http.patch<ScheduledBooking>(`${this._url}/scheduled-bookings/${id}/complete`, {}).pipe(
      map(obj => new ScheduledBooking(obj))
    );
  }

  /**
   * Mark booking as expired
   */
  expire(id: number): Observable<ScheduledBooking> {
    return this._http.patch<ScheduledBooking>(`${this._url}/scheduled-bookings/${id}/expire`, {}).pipe(
      map(obj => new ScheduledBooking(obj))
    );
  }

  /**
   * Get booking statistics
   */
  getStatistics(fromDate?: Date, toDate?: Date): Observable<any> {
    const params: any = {};
    if (fromDate) params.fromDate = fromDate.toISOString();
    if (toDate) params.toDate = toDate.toISOString();
    
    return this._http.get<any>(`${this._url}/scheduled-bookings/statistics`, { params });
  }

  /**
   * Get bookings for a specific date range
   */
  getByDateRange(startDate: Date, endDate: Date): Observable<ScheduledBooking[]> {
    const params = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
    return this._http.get<ScheduledBooking[]>(`${this._url}/scheduled-bookings/date-range`, { params }).pipe(
      map(obj => obj.map(xx => new ScheduledBooking(xx)))
    );
  }
}